/**
 * Per-user appearance engine (slice D). Pure functions: a user's accent / font / radius / sidebar
 * choice becomes a map of CSS custom properties layered over the Teal tokens in styles/tokens.css.
 * Colours are RGB channel triples ("15 110 86") like the tokens. The accent is AA-corrected: it is
 * moved toward black (light) / white (dark) in 8% steps until text on it, and it on the surface,
 * reach 4.5:1 -- a user can never pick an unreadable theme. Fonts are all local/system stacks
 * (no third-party font request).
 */
export type RGB = [number, number, number]
export type RadiusPref = 'sharp' | 'default' | 'round'
export type SidebarPref = 'light' | 'teal' | 'slate' | 'ink'
export type FontPref = 'inter' | 'system' | 'serif'
export type HeadingFontPref = 'same' | 'serif' | 'system'

export interface AppearancePrefs {
  accent: string | null          // preset key, '#rrggbb' custom, or null = organisation / Teal default
  font: FontPref
  heading_font: HeadingFontPref
  radius: RadiusPref
  sidebar: SidebarPref
}
export const APPEARANCE_DEFAULTS: AppearancePrefs = { accent: null, font: 'inter', heading_font: 'same', radius: 'default', sidebar: 'light' }

export const ACCENT_PRESETS: { key: string; name: string; hex: string }[] = [
  { key: 'teal', name: 'Teal', hex: '#0f6e56' },
  { key: 'blue', name: 'Blue', hex: '#1d4ed8' },
  { key: 'indigo', name: 'Indigo', hex: '#4338ca' },
  { key: 'violet', name: 'Violet', hex: '#6d28d9' },
  { key: 'rose', name: 'Rose', hex: '#be123c' },
  { key: 'coral', name: 'Coral', hex: '#c2410c' },
  { key: 'amber', name: 'Amber', hex: '#92400e' },
  { key: 'slate', name: 'Slate', hex: '#334155' },
]

export function hexToRgb(hex: string): RGB | null {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim())
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null
}
const ch = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4 }
export function luminance([r, g, b]: RGB) { return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b) }
export function contrast(a: RGB, b: RGB) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}
const mix = (a: RGB, b: RGB, t: number): RGB => [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t)) as RGB
const triple = (c: RGB) => c.join(' ')
const WHITE: RGB = [255, 255, 255]
const BLACK: RGB = [0, 0, 0]
const CARD_LIGHT: RGB = [255, 255, 255]
const CARD_DARK: RGB = [14, 33, 29]

/** Move `c` toward `toward` in 8% steps until it reaches `min` contrast against `bg`. */
function fix(c: RGB, bg: RGB, toward: RGB, min = 4.5): RGB {
  let cur = c
  for (let i = 0; i < 13 && contrast(cur, bg) < min; i++) cur = mix(cur, toward, 0.08)
  return cur
}

export function resolveAccent(accent: string | null): string | null {
  if (!accent) return null
  const p = ACCENT_PRESETS.find((x) => x.key === accent)
  const hex = p ? p.hex : accent
  return hexToRgb(hex) ? hex : null
}

/** Colour variables for one mode. Empty = keep the stock Teal tokens. */
export function colourVars(accent: string | null, mode: 'light' | 'dark'): Record<string, string> {
  const hex = resolveAccent(accent)
  const raw = hex ? hexToRgb(hex) : null
  if (!raw) return {}
  // Fill: white text must read on it, in both modes.
  const primary = fix(raw, WHITE, BLACK)
  const hover = mix(primary, BLACK, 0.2)
  // Link / interaction colour: readable on the card surface of the current mode.
  const link = mode === 'light' ? fix(raw, CARD_LIGHT, BLACK) : fix(mix(raw, WHITE, 0.35), CARD_DARK, WHITE)
  const subtle = mode === 'light' ? mix(link, WHITE, 0.9) : mix(link, CARD_DARK, 0.78)
  const soft = mode === 'light' ? mix(link, WHITE, 0.6) : mix(link, CARD_DARK, 0.6)
  return {
    '--primary': triple(primary), '--primary-hover': triple(hover), '--primary-foreground': '255 255 255',
    '--brand-accent': triple(link), '--brand-accent-subtle': triple(subtle), '--brand-accent-soft': triple(soft),
    '--ring': triple(link), '--surface-selected': triple(subtle),
  }
}

const RADIUS: Record<RadiusPref, [string, string, string]> = {
  sharp: ['0.1875rem', '0.375rem', '0.5rem'],
  default: ['0.5rem', '0.75rem', '1rem'],
  round: ['0.75rem', '1.125rem', '1.5rem'],
}
const FONTS: Record<FontPref, string> = {
  inter: "'Inter Variable', 'Inter Fallback', ui-sans-serif, system-ui, sans-serif",
  system: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', ui-serif, serif",
}
const SIDEBARS: Record<SidebarPref, Record<string, string>> = {
  light: {},
  teal: {},
  slate: { '--nav': '30 41 59', '--nav-active': '51 65 85', '--nav-line': '51 65 85', '--nav-content': '203 213 225', '--nav-indicator': '148 163 184' },
  ink: { '--nav': '17 24 39', '--nav-active': '31 41 55', '--nav-line': '38 48 66', '--nav-content': '203 213 225', '--nav-indicator': '148 163 184' },
}

export interface ThemeBundle { light: Record<string, string>; dark: Record<string, string>; layout: Record<string, string> }

/** Non-colour variables (identical in both modes) + the colour maps for each mode. */
export function computeTheme(p: AppearancePrefs): ThemeBundle {
  const [r, rc, ro] = RADIUS[p.radius] ?? RADIUS.default
  const layout: Record<string, string> = { '--radius': r, '--radius-card': rc, '--radius-overlay': ro }
  if (p.font !== 'inter') layout['--font-sans'] = FONTS[p.font] ?? FONTS.inter
  if (p.heading_font !== 'same') layout['--font-heading'] = p.heading_font === 'serif' ? FONTS.serif : FONTS.system
  Object.assign(layout, SIDEBARS[p.sidebar] ?? {})
  return { light: colourVars(p.accent, 'light'), dark: colourVars(p.accent, 'dark'), layout }
}

const MANAGED_KEY = 'bigroi.themevars'
let applied: string[] = []

export function applyTheme(bundle: ThemeBundle, mode: 'light' | 'dark', sidebar: SidebarPref) {
  const root = document.documentElement
  applied.forEach((k) => root.style.removeProperty(k))
  const vars = { ...bundle.layout, ...bundle[mode] }
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v)
  applied = Object.keys(vars)
  root.setAttribute('data-sidebar', sidebar)
  try { localStorage.setItem(MANAGED_KEY, JSON.stringify({ ...bundle, sidebar })) } catch { /* storage unavailable */ }
}

/**
 * Platform theme (SUPER_ADMIN → Platform → Theme, `GET /api/v1/platform/theme`, public so the
 * login page can paint it). Resolution order: platform default → organisation branding accent (if
 * `allow_org_override`) → the user's own choice (if `allow_user_override`). A user field still at
 * its stock default (accent null, theme 'system', density null…) means "inherit".
 */
export interface PlatformTheme extends AppearancePrefs {
  mode: 'light' | 'dark' | 'system'
  density: 'comfortable' | 'compact'
  login_style: 'white' | 'brand'
  allow_org_override: boolean
  allow_user_override: boolean
}
export const PLATFORM_THEME_DEFAULTS: PlatformTheme = {
  mode: 'light', accent: '#0F6E56', font: 'inter', heading_font: 'same', density: 'comfortable',
  radius: 'default', sidebar: 'light', login_style: 'white', allow_org_override: true, allow_user_override: true,
}

export function resolveAppearance(
  platform: PlatformTheme,
  orgAccent: string | null,
  user: (AppearancePrefs & { theme: 'light' | 'dark' | 'system'; density: 'comfortable' | 'compact' | null }) | null,
): AppearancePrefs & { mode: 'light' | 'dark' | 'system'; density: 'comfortable' | 'compact' | null } {
  const orgOk = platform.allow_org_override && !!orgAccent && !!hexToRgb(orgAccent) && contrast(hexToRgb(orgAccent)!, WHITE) >= 4.5
  const u = platform.allow_user_override ? user : null
  const pick = <K extends keyof AppearancePrefs>(k: K): AppearancePrefs[K] =>
    u && u[k] !== APPEARANCE_DEFAULTS[k] ? u[k] : platform[k]
  return {
    accent: (u?.accent ?? null) || (orgOk ? orgAccent : null) || platform.accent,
    font: pick('font'), heading_font: pick('heading_font'), radius: pick('radius'), sidebar: pick('sidebar'),
    mode: u && u.theme !== 'system' ? u.theme : platform.mode,
    density: u?.density ?? (platform.density === 'compact' ? 'compact' : null),
  }
}
